<script setup lang="ts">
  import { computed, ref } from 'vue';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import { useAssistantStore } from '@/composition/assistant/use-assistant-store';
  import { useToastObstacle } from '@/composition/use-toast-obstacle';

  /**
   * A newer version is out (plan §4.2): one card, one button. Toasts stack
   * above it, and on phones it sits above the GPTHelp bubble and the pages'
   * fixed bottom bars.
   */
  const emit = defineEmits<{ (e: 'update'): void }>();

  const assistant = useAssistantStore();
  const root = ref<HTMLElement | null>(null);
  useToastObstacle(root);

  /**
   * The «Asistente» overlay or sheet covers the page: the prompt waits under
   * its scrim (it would cover the composer) and comes back when it closes.
   */
  const underAssistant = computed(
    () => assistant.shown && assistant.tier !== 'docked'
  );
</script>

<template>
  <div
    ref="root"
    class="v2-shell-update-prompt hidden-print-only"
    :class="{ 'is-under-assistant': underAssistant }"
    role="alert"
  >
    <div class="prompt-text">
      <span class="prompt-title">{{ $t('shell.update.title') }}</span>
      <span class="prompt-hint">{{ $t('shell.update.hint') }}</span>
    </div>
    <PosUiButton
      class="prompt-action"
      color="neutral"
      variant="solid"
      icon="refresh"
      @click="emit('update')"
      >{{ $t('shell.update.action') }}</PosUiButton
    >
  </div>
</template>

<style scoped lang="scss">
  .v2-shell-update-prompt {
    @apply tw-fixed tw-bottom-4 tw-flex tw-w-max tw-items-center tw-rounded-xl tw-border tw-border-solid tw-border-neutral-200 tw-bg-surface tw-p-4 tw-shadow-lg;
    z-index: 10;
    // Centred in the page, left of the docked «Asistente» panel.
    left: calc((100vw - var(--assistant-inset-right, 0px)) / 2);
    max-width: calc(100vw - 32px - var(--assistant-inset-right, 0px));
    transform: translateX(-50%);

    // Below the «Asistente» scrim (z 7) while an overlay or sheet is open.
    &.is-under-assistant {
      z-index: 6;
    }

    // Phones: full width, like the toasts, and 84 px up on every page: above
    // the GPTHelp bubble (fixed bottom-left) where it shows and above the
    // pages' fixed bottom bars (POS tabs and footers, the Menú's selection
    // bar). The prompt cannot be dismissed, so it must never cover them.
    @media (max-width: 599px) {
      left: 16px;
      right: 16px;
      bottom: 84px;
      width: auto;
      transform: none;
    }

    .prompt-text {
      @apply tw-mr-4 tw-flex tw-min-w-0 tw-flex-col;
    }

    .prompt-title {
      @apply tw-text-sm tw-font-semibold tw-text-neutral-900;
    }

    .prompt-hint {
      @apply tw-mt-0.5 tw-text-xs tw-text-neutral-500;
    }

    .prompt-action {
      @apply tw-shrink-0;
    }
  }
</style>
